export function hexToHSLAndDarken(
  hexColor: string,
  darkerPercentage: number,
  opacity = 1,
): string {
  // Convert hex to RGB
  const rgb = hexToRGB(hexColor);

  // Convert RGB to HSL
  const hsl = rgbToHSL(rgb.r, rgb.g, rgb.b);

  // Darken the lightness by the specified percentage
  const darkenedHSL = darkenHSL(hsl, darkerPercentage);

  return formatHSL(darkenedHSL, opacity);
}

/**
 * Adds an alpha channel to a hex color based on an opacity percentage.
 * @param hexColor - Color in hex format, e.g. '#0099cc' or '#09c'.
 * @param opacityPercentage - Desired opacity from 0 to 100.
 * @returns Color in '#RRGGBBAA' format.
 */
export function hexWithOpacity(
  hexColor: string,
  opacityPercentage: number,
): string {
  // Remove the leading '#' if present
  let hex = hexColor.replace(/^#/, '');

  // Expand shorthand form (e.g. '09c' → '0099cc')
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('');
  }

  // Validate we now have exactly 6 hex digits
  if (hex.length !== 6) {
    throw new Error(`Invalid color format: ${hexColor}`);
  }

  // Clamp opacity between 0 and 100
  const pct = Math.max(0, Math.min(100, opacityPercentage));

  // Convert percentage to an alpha value (0–255), then to a 2-digit hex string
  const alpha = Math.round((pct / 100) * 255);
  const alphaHex = alpha.toString(16).padStart(2, '0').toUpperCase();

  // Return full hex with alpha channel
  return `#${hex.toUpperCase()}${alphaHex}`;
}

function hexToRGB(hex: string): { r: number; g: number; b: number } {
  // Remove the hash symbol if it exists
  hex = hex.replace(/^#/, '');

  // If the hex code is in the shorthand format (e.g., #fff)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('');
  }

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

function rgbToHSL(
  r: number,
  g: number,
  b: number,
): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0,
    s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function darkenHSL(
  hsl: { h: number; s: number; l: number },
  darkerPercentage: number,
): { h: number; s: number; l: number } {
  return {
    ...hsl,
    l: Math.max(0, hsl.l - Math.round((hsl.l * darkerPercentage) / 100)),
  };
}

function formatHSL(
  hsl: { h: number; s: number; l: number },
  opacity: number,
): string {
  const alpha = Math.max(0, Math.min(1, opacity)); // Ensure opacity is between 0 and 1
  return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${alpha})`;
}
